import { ITagOption, IClientOption } from 'components/SingleTask/taskAside'
import { ActionType, ISetTaskFilter, ISetTaskGroupState, ITask, ITaskGroupState, ITaskMassages } from './model'
import createReducer from '../createReducer'
import { Action, DEFAULT_ENTITY_ITEM_LIST, DEFAULT_ITEM_LIST, IEntityList, IItemList } from '../model'
import { IClientProduct } from '../companies/model'

export interface ITaskManager {
  categories: ITagOption[]
  statuses: ITagOption[]
  clients: IClientOption[]
  clientProducts: IEntityList<IClientProduct>
  tasks: {
    urgent: IItemList<ITask> // Срочные задачи менеджеров / Мои срочные задачи
    managers: IItemList<ITask> // Задачи менеджеров / Мои задачи
    clientManagers: IItemList<ITask>
    clients: IItemList<ITask> // Задачи клиентов
    done: IItemList<ITask> // Выполненные
    managersDone: IItemList<ITask> // Выполненные
  }
  task: ITask | null
  taskLoading: boolean
  taskMassages: ITaskMassages
  taskGroupState: {
    urgent: ITaskGroupState
    managers: ITaskGroupState
    clientManagers: ITaskGroupState
    clients: ITaskGroupState
    done: ITaskGroupState
    managersDone: ITaskGroupState
  }
  filter: {
    accountManagerIds: number[]
    advertiserIds: number[]
    agencyIds: number[]
    // В фильтре тасок теперь используем clientIds вместо advertiserIds и agencyIds
    clientIds: number[]
    statusIds: number[]
    categoryIds: number[]
    supportTypeIds: number[]
    productIds: number[]
  }
}

const DEFAULT_TASK_GROUP_STATE = {
  collapsed: false,
  isFullList: false,
}

const DEFAULT_TASK_FILTERS = {
  accountManagerIds: [],
  advertiserIds: [],
  agencyIds: [],
  clientIds: [],
  statusIds: [],
  categoryIds: [],
  supportTypeIds: [],
  productIds: [],
  createdFrom: [],
  taskId: [],
}

const defaultState: ITaskManager = {
  categories: [],
  statuses: [],
  clients: [],
  clientProducts: DEFAULT_ENTITY_ITEM_LIST,
  tasks: {
    urgent: DEFAULT_ITEM_LIST,
    managers: DEFAULT_ITEM_LIST,
    clientManagers: DEFAULT_ITEM_LIST,
    clients: DEFAULT_ITEM_LIST,
    done: DEFAULT_ITEM_LIST,
    managersDone: DEFAULT_ITEM_LIST,
  },
  task: null,
  taskLoading: false,
  taskMassages: {
    data: [],
    totalCount: 0,
    newMessages: 0,
  },
  taskGroupState: {
    urgent: DEFAULT_TASK_GROUP_STATE,
    managers: DEFAULT_TASK_GROUP_STATE,
    clientManagers: {
      ...DEFAULT_TASK_GROUP_STATE,
      collapsed: true,
    },
    clients: DEFAULT_TASK_GROUP_STATE,
    done: DEFAULT_TASK_GROUP_STATE,
    managersDone: {
      ...DEFAULT_TASK_GROUP_STATE,
      collapsed: true,
    },
  },
  filter: DEFAULT_TASK_FILTERS,
}

export const taskManagerReducer = createReducer<ITaskManager>(defaultState, {
  [ActionType.RESET_TASK_MANAGER]() {
    return { ...defaultState }
  },

  [ActionType.SET_CATEGORIES](state: ITaskManager, action: Action<ITagOption[]>) {
    return { ...state, categories: action.payload }
  },

  [ActionType.SET_STATUSES](state: ITaskManager, action: Action<ITagOption[]>) {
    return { ...state, statuses: action.payload }
  },

  [ActionType.SET_ACCOUNT_CLIENTS](state: ITaskManager, action: Action<IClientOption[]>) {
    return { ...state, clients: action.payload }
  },

  [ActionType.SET_URGENT_TASKS](state: ITaskManager, action: Action<ITask[]>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        urgent: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_ACCOUNT_MANAGERS_TASKS](state: ITaskManager, action: Action<ITask[]>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        managers: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_CLIENTS_TASKS](state: ITaskManager, action: Action<IItemList<ITask>>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        clients: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_CLIENT_MANAGERS_TASKS](state: ITaskManager, action: Action<ITask[]>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        clientManagers: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_DONE_TASKS](state: ITaskManager, action: Action<ITask[]>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        done: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_MANAGERS_DONE_TASKS](state: ITaskManager, action: Action<ITask[]>) {
    return {
      ...state,
      tasks: {
        ...state.tasks,
        managersDone: {
          ...action.payload,
          loading: false,
        },
      },
    }
  },

  [ActionType.SET_TASK](state: ITaskManager, action: Action<ITask | null>) {
    return { ...state, task: action.payload, taskLoading: false }
  },

  [ActionType.CHANGE_ACTIVE_TASK](state: ITaskManager, action: Action<{ id: number }>) {
    if (state.task?.id !== action.payload.id) {
      return { ...state, taskLoading: true }
    }
    return { ...state }
  },

  [ActionType.SET_TASK_MESSAGES](state: ITaskManager, action: Action<ITaskMassages | null>) {
    return { ...state, taskMassages: action.payload }
  },

  [ActionType.SET_ALL_TASKS](state: ITaskManager, action: Action) {
    return { ...state, tasks: action.payload }
  },

  [ActionType.SET_COLLAPSED_TASK_GROUP](state: ITaskManager, action: Action<ISetTaskGroupState>) {
    const { payload } = action
    return {
      ...state,
      taskGroupState: {
        ...state.taskGroupState,
        [payload.groupType]: {
          collapsed: payload.state,
          isFullList: state.taskGroupState[payload.groupType].isFullList,
        },
      },
    }
  },

  [ActionType.SET_FULL_LIST_TASK_GROUP](state: ITaskManager, action: Action<ISetTaskGroupState>) {
    const { payload } = action
    return {
      ...state,
      taskGroupState: {
        ...state.taskGroupState,
        [payload.groupType]: {
          collapsed: state.taskGroupState[payload.groupType].collapsed,
          isFullList: payload.state,
        },
      },
    }
  },

  [ActionType.ADD_TASK_FILTER](state: ITaskManager, action: Action<ISetTaskFilter>) {
    const { payload } = action
    return {
      ...state,
      filter: {
        ...state.filter,
        [payload.type]: [...state.filter[payload.type], payload.id],
      },
    }
  },

  [ActionType.REMOVE_TASK_FILTER](state: ITaskManager, action: Action<ISetTaskFilter>) {
    const { payload } = action
    return {
      ...state,
      filter: {
        ...state.filter,
        [payload.type]: state.filter[payload.type].filter(id => id !== payload.id),
      },
    }
  },

  [ActionType.CLEAR_ALL_TASK_FILTERS](state: ITaskManager) {
    return {
      ...state,
      filter: {
        ...DEFAULT_TASK_FILTERS,
      },
    }
  },

  [ActionType.UPDATE_FILTERS_BY_QUERY](state: ITaskManager, action: Action) {
    const { payload } = action

    return {
      ...state,
      filter: {
        ...DEFAULT_TASK_FILTERS,
        ...payload,
      },
    }
  },

  [ActionType.GET_TASK_CLIENT_PRODUCTS](state: ITaskManager) {
    return {
      ...state,
      clientProducts: {
        ...DEFAULT_ENTITY_ITEM_LIST,
      },
    }
  },
  [ActionType.SET_TASK_CLIENT_PRODUCTS](state: ITaskManager, action: Action) {
    const { payload } = action
    return {
      ...state,
      clientProducts: {
        ...payload,
        loading: false,
      },
    }
  },
})
